const HeaderScrollPosition = (): () => void => {
    let lastScrollTop = 0; // Previous scroll position
    let hasScrolledDown = false; // Flag to track if user has scrolled down
    let hasScrolledUp = false; // Flag to track if user has scrolled up after scrolling down

    const isAsm = document.querySelector('body.asm > .page-content');
    const header = document.querySelector('header:not(.default)') as HTMLElement; // Header element
    const content = document.querySelector('.page-content') as HTMLElement; // Main content element

    if (!header || !content) {
        return () => {
            // No operation needed as header or content is not found
        };
    }

    // Helper function to set content margin
    const setContentMargin = (height: number): void => {
        content.style.marginTop = `${height}px`;
    };

    // Helper function to reset the header state
    const resetHeaderState = (): void => {
        hasScrolledUp = false;
        hasScrolledDown = false;
        header.classList.remove('fixed', 'hidden', 'temp-hidden');
        if (isAsm) {
            setContentMargin(-24);
        } else {
            setContentMargin(0);
        }
    };

    // Helper function to hide the header
    const hideHeader = (height: number): void => {
        header.classList.add('hidden');
        header.classList.remove('temp-hidden');
        header.classList.remove('fixed');
        setContentMargin(height);
        if (isAsm) {
            setContentMargin(height - 24);
        } else {
            setContentMargin(height);
        }
    };

    // Helper function to reveal the header
    const revealHeader = (height: number, elementAboveHeaderHeight = 0): void => {
        // Only remove 'temp-hidden' if it was added previously
        if (header.classList.contains('temp-hidden')) {
            header.classList.remove('temp-hidden');
            header.classList.add('hidden');
        }

        let contentMarginFromHeight = height;
        header.classList.remove('hidden');
        if (elementAboveHeaderHeight) {
            header.classList.remove('fixed');
            contentMarginFromHeight = 0;
        } else {
            header.classList.add('fixed');
        }

        if (isAsm) {
            setContentMargin(contentMarginFromHeight - 24);
        } else {
            setContentMargin(contentMarginFromHeight);
        }
    };

    const getAllElementsAboveHeaderHeight = (): number => {
        let result = 0;
        const acknowledgementEl = document.querySelector('.acknowledgement');
        const hasAcknowledgement = acknowledgementEl?.classList.contains('active');
        if (hasAcknowledgement
            && !acknowledgementEl?.classList.contains('hide')) {
            result = result + ((acknowledgementEl as HTMLElement).offsetHeight || 0);
        }
        const alertBannerEl = document.querySelector('.alert-banner');
        if (alertBannerEl
            && !alertBannerEl?.classList.contains('hide')) {
            result = result + ((alertBannerEl as HTMLElement).offsetHeight || 0);
        }

        return result;
    };

    const handleScroll = (): void => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const currentHeaderHeight = header.offsetHeight || 120; // Dynamically get the header height

        const elementsAboveHeaderHeight = getAllElementsAboveHeaderHeight();

        // Reset flags and header when at the very top of the page (scrollTop === 0)
        if (scrollTop === 0) {
            resetHeaderState();
            return;
        }

        // Track first scroll down after passing height of the header
        if (!hasScrolledDown && scrollTop > currentHeaderHeight + elementsAboveHeaderHeight) {
            hasScrolledDown = true;
            header.classList.add('temp-hidden'); // Hide the header after header height
            setContentMargin(currentHeaderHeight); // Adjust content margin accordingly
            if (isAsm) {
                setContentMargin(currentHeaderHeight - 24);
            } else {
                setContentMargin(currentHeaderHeight);
            }
        }

        // Trigger header reveal when the user scrolls up for the first time after scrolling down
        if (hasScrolledDown && !hasScrolledUp && scrollTop < lastScrollTop) {
            hasScrolledUp = true;
            if (scrollTop < elementsAboveHeaderHeight) {
                revealHeader(currentHeaderHeight, elementsAboveHeaderHeight);
            } else {
                revealHeader(currentHeaderHeight);
            }
        }

        // Handle scrolling behavior after the first scroll-up
        if (hasScrolledUp && scrollTop > currentHeaderHeight) {
            // Scrolling down: Hide the header
            if (scrollTop > lastScrollTop && !header.classList.contains('hidden')) {
                hideHeader(currentHeaderHeight);
            }

            // Scrolling up: Reveal the header again
            if (scrollTop < lastScrollTop) {
                if (header.classList.contains('hidden')) {
                    revealHeader(currentHeaderHeight);
                } else if (scrollTop < elementsAboveHeaderHeight) {
                    revealHeader(currentHeaderHeight, elementsAboveHeaderHeight);
                }
            }
        }

        // Update last scroll position for the next scroll event
        lastScrollTop = Math.max(scrollTop, 0); // Ensure lastScrollTop doesn't go below 0
    };

    const onFocusHandler = (): void => {
        const elementsAboveHeaderHeight = getAllElementsAboveHeaderHeight();
        if (lastScrollTop > elementsAboveHeaderHeight) {
            revealHeader(header.offsetHeight); // Reveal the header on focus
        }
    };

    const onBlurHandler = (): void => {
        const elementsAboveHeaderHeight = getAllElementsAboveHeaderHeight();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (scrollTop > header.offsetHeight && lastScrollTop > elementsAboveHeaderHeight) {
            hideHeader(header.offsetHeight);
        }
    };

    // Add the scroll event listener
    window.addEventListener('scroll', handleScroll);

    window.addEventListener('load', () => {
        if (!header) {
            return;
        }

        const focusableElements = header.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

        focusableElements.forEach(element => {
            element.addEventListener('focus', onFocusHandler);

            element.addEventListener('blur', onBlurHandler);
        });
    });

    // Cleanup function to remove event listeners
    return (): void => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('load', () => {
            if (!header) {
                return;
            }

            const focusableElements = header.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

            focusableElements.forEach(element => {
                element.removeEventListener('focus', onFocusHandler);
                element.removeEventListener('blur', onBlurHandler);
            });
        });
    };
};

// Export the function for use in other parts of the app
export default HeaderScrollPosition;
